import React, { useRef, useState } from 'react';
import Icons from '@/const/icons';
import { Conversation } from '@/interfaces/inbox';
import { Button, Icon, Typography } from '../common';
import { MessageDate } from './MessageDate';
import { AvatarName } from '../dashboard';
import { inboxService } from '@/api/inbox';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { MessageContent } from './MessageContent';
import { useQueryClient } from '@tanstack/react-query';
import { GET_CONVERSATIONS, awsService } from '@/api';
import { useRouter } from 'next/router';
import AppRoutes from '@/const/routes';

interface MessageReferenceProps extends Conversation {
	applicationId?: number;
	conversationId?: string;
}

const VALID_FILE_TYPES = [
	'image/jpeg',
	'image/jpg',
	'image/png',
	'application/pdf',
];
const MAX_FILE_SIZE = 25 * 1000 * 1024;

export const MessageReference = ({
	role,
	content,
	createdAt,
	firstname,
	lastname,
	profile_picture,
	document_name,
	action_required_id,
	case_id,
	applicationId,
	conversationId,
	me,
	action_required_complete,
	referenced_message_id,
	referenced_message,
	type,
}: MessageReferenceProps) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [uploaded, setUploaded] = useState(false);
	const { toastError, toastSuccess } = useNotification();
	const queryClient = useQueryClient();
	const router = useRouter();

	const isDocumentUploadType =
		type === 'DOCUMENT' || type === 'REQUEST_DOCUMENT';
	const isSignType = type === 'TEXT' && !!action_required_id;

	const handleSignRedirect = () => {
		if (!case_id) return;
		router.push(`${AppRoutes.CLIENTAPP_MY_CASES}/${case_id}`);
	};

	React.useEffect(() => {
		setUploaded(!!action_required_complete);
	}, [action_required_complete]);

	const handleUpload = async (file: File) => {
		if (file.size > MAX_FILE_SIZE) {
			toastError('The file is too heavy. Max 25 MB.');
			return;
		}
		if (!VALID_FILE_TYPES.includes(file.type)) {
			toastError('Invalid format. Use pdf, png, jpg or jpeg.');
			return;
		}
		if (!applicationId || !action_required_id) return;

		try {
			setIsUploading(true);
			const response = await awsService.uploadImage(file);
			const url = response.data.data.location;
			const extension = url.split('.').pop();

			if (conversationId) {
				await inboxService.postAddMessage({
					conversationId,
					content: `${file.name}`,
					is_bot: false,
					action_required_id: action_required_id,
					document_url: url,
					document_name: `${document_name}.${extension}`,
				});
			}

			setUploaded(true);
			queryClient.invalidateQueries({ queryKey: [GET_CONVERSATIONS] });
			toastSuccess('Document uploaded successfully');
		} catch (error) {
			toastError(formatErrorMessage((error as any).message));
		} finally {
			setIsUploading(false);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			handleUpload(file);
			e.target.value = '';
		}
	};

	return (
		<div className="bg-green-secondary rounded-xl p-3">
			{/* Referenced message block */}
			{referenced_message_id && referenced_message && (
				<div className="bg-grey-10 rounded-tr-[16px] rounded-b-[16px] border-l-2 border-primary p-3 mb-3">
					<AvatarName
						isBold
						photoUrl={referenced_message.profile_picture || ''}
						size="extra-small"
						firstname={referenced_message.firstname || 'Unknown'}
						lastname={referenced_message.lastname || ''}
					/>
					<div className="mt-2">
						<MessageContent content={referenced_message.content} />
					</div>
				</div>
			)}
			{/* Document block */}
			{document_name && (
				<div className="bg-grey-10 rounded-tr-[16px] rounded-b-[16px] border-l-2 border-primary p-3">
					<div className="flex items-center gap-2">
						<Icon
							src={Icons.documentFile}
							width={16}
							height={16}
							className="text-green-60"
						/>
						<div className="flex flex-col">
							<Typography type="regular-14">{document_name}</Typography>
							<MessageDate date={createdAt} />
						</div>
					</div>
				</div>
			)}
			<AvatarName
				isBold
				photoUrl={profile_picture}
				size="extra-small"
				firstname={firstname}
				lastname={lastname}
				className="my-3"
			/>
			<div className="bg-green-140 rounded-tr-[16px] rounded-b-[16px] p-3">
				<MessageContent content={content} />
			</div>
			{action_required_id && !referenced_message_id && isDocumentUploadType && (
				<>
					<input
						ref={fileInputRef}
						type="file"
						accept={VALID_FILE_TYPES.join(',')}
						className="hidden"
						onChange={handleFileChange}
					/>
					<Button
						disabled={(me && role === 'lawyer') || isUploading || uploaded}
						decoration="primary"
						size="small"
						className="mt-3"
						type="button"
						onClick={(e) => {
							e?.stopPropagation();
							fileInputRef.current?.click();
						}}
					>
						{uploaded ? 'Uploaded' : isUploading ? 'Uploading...' : 'Upload'}
					</Button>
				</>
			)}
			{isSignType && !referenced_message_id && (
				<Button
					decoration="primary"
					size="small"
					className="mt-3"
					type="button"
					onClick={(e) => {
						e?.stopPropagation();
						handleSignRedirect();
					}}
				>
					{`Sign${document_name ? ` ${document_name}` : ''}`}
				</Button>
			)}
		</div>
	);
};
